/**
 * МОБИЛЬНАЯ РАСКЛАДКА (≤640px) — Android-оболочка (Capacitor) и узкие
 * браузеры (спека inbox/mobile-layout/SPEC.md, Антон 2026-09-20 15:21).
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ CSS-ФАЙЛ, А НЕ РАЗБРОСАННЫЕ ПРАВКИ:
 * мобильная раскладка — это набор взаимосвязанных правил поверх
 * существующих классов (шапка, список, рельс, лента). Держать их рядом с
 * десктопными CSS-строками значило бы при каждом чтении десктопного
 * правила проверять «а не перебито ли оно на мобильном». Здесь ВСЁ,
 * что касается ≤640px, — один файл, один @media-блок; десктоп не трогаем
 * (правило постановки: изменения только через медиазапросы / условный
 * рендер по narrow).
 *
 * ПОЧЕМУ CSS, А НЕ INLINE: та же причина, что у roomListCss — inline-стиль
 * не умеет @media. Условные стили в JS (useNarrowScreen) используем только
 * там, где меняется РАЗМЕТКА, а не оформление.
 *
 * Все правила сидят в одном @media (max-width: 640px) и большинство —
 * под классом-маркером `alg-mobile`, который App ставит на корневой div:
 * так правила не срабатывают случайно на планшетах с 640px ровно и не
 * протекают на десктоп даже при совпадении ширины.
 */
import { color, font, radius } from "./theme";

export const mobileCss = `
@media (max-width: 640px) {

  /* ===== КОРНЕВАЯ РАСКЛАДКА: список ИЛИ чат, оба на весь экран ===== */

  /* Рельс спейсов на мобильном не существует (спека: его заменяет строка
     спейсов над списком + меню «Пространства»). display:none, а не
     условный рендер: рельс — сложное поддерево, и App/RoomList его всё
     равно не монтируют на narrow; правило — страховка, если что-то
     просочится. */
  .alg-mobile .alg-rail { display: none; }

  /* Левая колонка — на всю ширину. !important нужен: inline-стили колонки
     задают flex: 0 0 <sidebarWidth>px (ширина, выбранная пользователем на
     десктопе), и без !important мобильная раскладка их не перебьёт. */
  .alg-mobile .alg-room-list-shell {
    width: 100% !important;
    flex: 1 1 auto !important;
    min-width: 0;
    min-height: 0;
  }
  .alg-mobile .alg-room-sidebar {
    width: 100% !important;
    flex: 1 1 auto !important;
    border-right: none;
    min-width: 0;
  }
  /* Ручка ресайза бессмысленна на тач-экране: тянуть нечего. */
  .alg-mobile [data-testid^="resize-"],
  .alg-mobile [data-testid="panel-resize-handle"] { display: none !important; }

  /* ===== ШАПКА СПИСКА: «аватар | название | + | ⚙️» → поиск+фильтр ===== */

  /* Мобильная шапка (правка Антона 2026-09-20 19:17, п.2; тач-цели
     уточнены 19:24 п.3): «+», ⚙️ и аватар — ровно 44×44px под палец.
     Аватар внутри кнопки остаётся 40px кружком: тач-цель — вся кнопка,
     а не видимый круг (padding дожимает до 44). */
  .alg-mobile .alg-mobile-header button {
    min-width: 44px;
    min-height: 44px;
  }
  /* Кнопка-аватар пришла из рельсы: у alg-rail-btn padding:0, поэтому
     40px круг даёт тач-цель 40px. Добавляем 2px воздуха с каждой
     стороны — круг визуально тот же, кнопка 44×44. */
  .alg-mobile .alg-mobile-header [data-testid="open-profile"] {
    padding: 2px;
  }

  /* Ряд текстовых кнопок «+ Комната»/«+ Личный чат»/«+ Пространство»
     на мобильном УДАЛЁН ИЗ РАЗМЕТКИ (правка Антона 2026-09-21): RoomList
     больше не рендерит его на narrow вовсе (см. {!narrow && …} вокруг
     actionRow), все три действия живут в меню кнопки «+» в шапке.
     CSS-класс alg-mobile-head-actions упразднён. */

  /* Поле поиска — 16px шрифтом: иначе iOS/Android WebView зумит экран
     при фокусе (спека: 16px в полях ввода). */
  .alg-mobile .alg-search-input {
    font-size: 16px !important;
    min-height: 40px;
  }

  /* Строка спейсов над списком — ЕДИНСТВЕННОЕ представление спейсов на
     мобильном (правка 19:17, п.3): текстовые вкладки убраны. Иконки
     круглые 40px с подписью спейса под каждой — иначе спейсы с похожими
     аватарами не различить. Выбранный: синяя рамка + синяя жирная
     подпись. Горизонтальная прокрутка, скроллбар скрыт. */
  .alg-mobile-spaces-bar {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 10px 6px;
    overflow-x: auto;
    overflow-y: hidden;
    border-bottom: 1px solid ${color.borderSoft};
    background: ${color.sidebarBg};
    flex: 0 0 auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    -webkit-overflow-scrolling: touch;
  }
  .alg-mobile-spaces-bar::-webkit-scrollbar { height: 0; display: none; }

  /* ===== СПИСОК ЧАТОВ ===== */

  .alg-mobile .alg-room-row {
    /* Тач-цель ≥44px (спека). Высота строки больше десктопных 60px —
       на телефоне строка и так занимает весь список, экономить высоту
       ценой промахов нельзя. */
    height: 64px;
    border-radius: 0;
    padding: 0 12px 0 12px;
  }
  .alg-mobile .alg-room-row.is-active {
    /* На мобильном «активная» строка — это открытый чат, но список тогда
       скрыт: подсветка нужна только на момент возврата (история).
       Оставляем ту же мягкую заливку, но без inset-полосы — она
       конфликтует с border-radius:0 на всю ширину. */
    box-shadow: none;
  }

  /* Подвал со счётчиком чатов на мобильном — мелкий серый текст,
     оставляем, но компактнее. */
  .alg-mobile .alg-mobile-sidebar-foot { font-size: ${font.micro}px; }

  /* ===== ОТКРЫТЫЙ ЧАТ НА ВЕСЬ ЭКРАН ===== */

  /* App на narrow рендерит ЛИБО список, ЛИБО комнату (условный рендер,
     см. App). Комната занимает всю ширину: s.main уже flex:1 1 auto,
     ничего дополнительного не нужно — правила ниже только уточняют
     мелочи. */

  /* Кнопка «назад» в шапке чата: тач-цель 44×44 (спека). */
  .alg-mobile-back {
    flex: 0 0 auto;
    width: 44px;
    height: 44px;
    margin-left: -8px; /* визуально прижать стрелку к левому краю */
    border: none;
    background: transparent;
    color: ${color.text};
    display: grid;
    place-items: center;
    cursor: pointer;
    border-radius: ${radius.circle};
    touch-action: manipulation;
  }
  .alg-mobile-back:active { background: ${color.hover}; }

  /* Шапка чата на мобильном: компактнее, аватар меньше, действия —
     только звонки (аудио/видео); «ещё» уходит… никуда: страница комнаты
     открывается тапом по имени (уже есть), «⋯» оставляем — он второй
     вход в ту же страницу и стоит один 32px. */
  .alg-mobile .alg-room-header {
    gap: 4px !important;
    padding: 6px 8px !important;
  }
  .alg-mobile .alg-room-header-avatar {
    width: 36px !important;
    height: 36px !important;
    flex-basis: 36px !important;
  }

  /* Лента и композер: отступы у́же, чтобы текст занимал ширину. */
  .alg-mobile .alg-mobile-timeline { padding: 8px 8px !important; }

  /* Поле ввода — 16px (анти-зум WebView), тач-цели кнопок ≥44px. */
  .alg-mobile textarea,
  .alg-mobile input[type="text"],
  .alg-mobile input[type="search"],
  .alg-mobile .alg-composer-input textarea,
  .alg-mobile .alg-composer-preview {
    font-size: 16px !important;
  }
  /* Кнопки композера (плюс/эмодзи/отправка) — 34px на десктопе, на
     мобильном ниже 44px тач-цель опускать нельзя (спека этап 5). */
  .alg-mobile .alg-composer-btn {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
  }
  /* Иконка внутри кнопки композера не растёт — 44px кнопка с той же
     пиктограммой читается так же, зря в неё не тыкнут. */
  .alg-mobile .alg-composer-bar { padding: 2px; }

  /* ===== ЭКРАН ЗВОНКА (WhatsApp-стиль, спека этап 4) ===== */

  /* На весь экран, поверх ВСЕГО (включая список, если бы он был виден):
     position:fixed inset:0. Видео собеседника фоном, своё превью — PiP,
     круглые контролы снизу, safe-area учтён в компоненте. z-index выше
     остальных мобильных слоёв (диалоги 50, меню 60–70): звонок важнее. */
  .alg-mobile .alg-call-screen {
    position: fixed !important;
    inset: 0 !important;
    z-index: 85 !important;
  }
  /* Медиазона — весь экран без внутренних полей: видео собеседника
     заполняет фон, как в WhatsApp/Telegram видеозвонке. Своё превью
     (PiP) и контролы позиционируются абсолютно поверх, им паддинги
     контейнера не нужны. */
  .alg-mobile .alg-call-grid { padding: 0 !important; gap: 0 !important; }
  /* Плитки сетки (3+ участников) на весь ряд без полей, без радиусов. */
  .alg-mobile .alg-call-grid [data-testid="call-tile"] { border-radius: 0 !important; }

  /* Мини-фрейм звонка на мобильном: компактнее, внизу справа,
     не перекрывает композер. */
  .alg-mobile .alg-call-miniframe {
    top: auto !important;
    bottom: 90px !important;
    right: 8px !important;
    width: 140px !important;
  }

  /* Контролы звонка — тач-цель ≥44px (спека этап 5). Кнопки уже 48px;
     стрелки-меню устройств меньше — растягиваем их до 44px, иначе на
     телефоне в них промахиваются. */
  .alg-mobile .alg-call-screen button { touch-action: manipulation; }

  /* ===== ДИАЛОГИ И МЕНЮ ===== */

  /* ВСЕ контекстные меню на мобильном — DROPDOWN «пирамидка» ВНИЗ ПОД
     элементом (уточнение Антона 2026-09-20 19:24, отменяет bottom-sheet
     19:23). Разметка — DropdownMenu/DropdownItem в RoomList; здесь
     каркас: прозрачный фуллскрин-ловец (тап мимо = закрыть; затемнения
     нет сознательно — dropdown не модален) и само меню, узкое у якоря.
     Классы без .alg-mobile: меню рендерится ТОЛЬКО на narrow (условный
     рендер), а @media и так ограничивает файл; z-index 70 — как был у
     шторки, выше диалогов (50) и ниже звонка (85). */
  .alg-dropdown-catcher {
    position: fixed;
    inset: 0;
    z-index: 70;
    background: transparent;
  }
  .alg-dropdown-menu {
    position: fixed;
    min-width: 200px;
    max-width: min(280px, calc(100vw - 16px));
    overflow-y: auto;
    background: ${color.bg};
    border: 1px solid ${color.border};
    border-radius: 12px;
    padding: 4px;
    box-shadow: 0 8px 28px rgba(0,0,0,0.28);
    -webkit-overflow-scrolling: touch;
  }
  .alg-dropdown-item {
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    min-height: 44px;
    padding: 8px 14px;
    border: none;
    border-radius: 8px;
    background: transparent;
    font: inherit;
    font-size: 16px;
    color: ${color.text};
    text-align: left;
    cursor: pointer;
    touch-action: manipulation;
  }
  .alg-dropdown-item:active { background: ${color.hover}; }

  /* Диалоги (создание комнаты и т.п.) на весь экран снизу — не центрированные
     окошки 380px: на 390px вьюпорте окно 380px с полями выглядит клаустрофобно. */
  .alg-mobile .alg-dialog-box {
    width: 100% !important;
    max-width: 100% !important;
    border-radius: ${radius.bubble}px ${radius.bubble}px 0 0 !important;
    border: none !important;
    max-height: 88dvh;
    overflow-y: auto;
  }
  .alg-mobile .alg-dialog-backdrop {
    align-items: flex-end !important;
    display: flex !important;
  }

  /* Баннер входящего звонка: на мобильном сверху на всю ширину.
     transform от инлайна конфликтует с left/right на всю ширину —
     убираем его и центрируем полями. */
  .alg-mobile .alg-incoming-call {
    left: 8px !important;
    right: 8px !important;
    transform: none !important;
    max-width: none !important;
  }

  /* Правая панель (страница комнаты / участники / тред): на весь экран
     поверх ленты. Компоненты сами рендерятся условно; здесь — габариты. */
  .alg-mobile .alg-room-page,
  .alg-mobile .alg-thread-panel,
  .alg-mobile .alg-member-panel {
    position: fixed !important;
    inset: 0 !important;
    width: 100% !important;
    flex-basis: auto !important;
    z-index: 30;
    border-left: none !important;
  }

  /* ===== ТАЧ-РАЗМЕРЫ (спека этап 5): все интерактивные элементы
     ≥44×44px на мобильном. Правила намеренно шире отдельных классов:
     пропустить одну кнопку в списке значит получить промах именно
     в ней. Специфичность подбираем ниже десктопных hover-правил, чтобы
     не сломать наведение на десктопе (эти правила внутри @media и под
     .alg-mobile — на десктоп не протекают). ===== */

  /* Кнопки в шапке чата (звонок/видео/ещё) — iconButton 32px → 44px. */
  .alg-mobile .alg-room-header-actions button {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
  }

  /* Иконка фильтра рядом с поиском — 34px → 44px. */
  .alg-mobile .alg-filter-button {
    width: 44px;
    height: 44px;
  }

  /* Пункты меню фильтра и контекстного меню комнаты — выше, под палец. */
  .alg-mobile .alg-filter-item,
  .alg-mobile .alg-filter-clear {
    min-height: 44px;
    font-size: 16px;
  }

  /* Плавающие меню (комната/спейс) на мобильном: пункты под палец и
     шрифт 16px — с ними же работают большие пальцы. */
  .alg-mobile [data-testid="room-menu"] button,
  .alg-mobile [data-testid="space-menu"] button {
    min-height: 44px;
    font-size: 16px;
  }

  /* Кнопки «Принять/Отклонить» в строке приглашения (invite-accept/decline)
     и в шапке открытого приглашения (header-accept/decline). */
  .alg-mobile [data-testid="invite-accept"],
  .alg-mobile [data-testid="invite-decline"],
  .alg-mobile [data-testid="header-accept"],
  .alg-mobile [data-testid="header-decline"] {
    min-height: 44px;
    font-size: 15px !important;
  }

  /* Просмотрщик медиа, верхняя панель ошибок, мини-фрейм — тач-цели. */
  .alg-mobile [data-testid^="media-viewer"] button,
  .alg-mobile [data-testid="call-mini"] button {
    min-width: 44px;
    min-height: 44px;
  }

  /* REM: базовый размер шрифта документа — 16px на мобильном (спека
     этап 5). Все rem-размеры отсчитываются от него; px-размеры токенов
     (font.body=15 и т.п.) этим НЕ меняются — ключевые поля переопределены
     точечно выше на 16px. Правило на html внутри @media — единственный
     способ задать rem-базу только для узких экранов. */

  /* Просмотрщик медиа и так absolute внутри s.main; на мобильном
     комната — весь экран, значит зритель на весь экран автоматически. */
}

/* База rem для узких экранов. Вынесено из общего блока выше, потому что
   селектор html не может быть потомком .alg-mobile — класс стоит на #root. */
@media (max-width: 640px) {
  html { font-size: 16px; }
}
`;
